import { IHttpApi } from '@cian/http-api';
import { ILogger } from '@cian/logger';
import { IContext, ISaveSearchProps } from '@cian/save-search-widget';
import { ModalWindow } from '@cian/ui-kit/modal';
import loadable from '@loadable/component';
import * as React from 'react';

import * as styles from './SaveSearchModal.css';
import { WidgetErrorBoundary } from './components/WidgetErrorBoundary';
import { WidgetFallback } from './components/WidgetFallback';

const SaveSearchWidgetLoadable = loadable.lib(() => import('@cian/save-search-widget'), {
  fallback: <WidgetFallback />,
});

export interface ISaveSearchModalProps {
  httpApi: IHttpApi;
  logger: ILogger;
  subdomain: string;
  jsonQuery: unknown;
  defaultTitle: string | null;
  userEmail: string | null;
  hideLicense?: boolean;
  open: boolean;
  onSubscribed?(parameters: Parameters<Required<ISaveSearchProps>['onSubscribed']>[0]): void;
  onClose(): void;
  modalTitle?: string;
  customSubmitButtonText?: string;
}

export const SaveSearchModal: React.FC<ISaveSearchModalProps> = ({
  hideLicense,
  httpApi,
  jsonQuery,
  logger,
  open: isOpen,
  subdomain,
  defaultTitle,
  userEmail,
  onClose,
  onSubscribed,
  modalTitle,
  customSubmitButtonText,
}) => {
  const widgetContext = React.useRef<IContext | null>(null);
  const handleRetry = React.useCallback(() => {
    SaveSearchWidgetLoadable.load();
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      widgetContext.current = null;
    }
  }, [isOpen]);

  const title = modalTitle || 'Добавить дом в избранное';
  const submitButtonText = customSubmitButtonText || 'Добавить дом в избранное';

  return (
    <ModalWindow
      open={isOpen}
      onClose={onClose}
      size="M"
      title={title}
      content={
        <div className={styles['container']}>
          <WidgetErrorBoundary logger={logger} onRetry={handleRetry}>
            <SaveSearchWidgetLoadable>
              {({ createContext, Provider, SaveSearch, EFrequency, ESource }) => {
                if (!widgetContext.current) {
                  widgetContext.current = createContext({
                    subdomain,
                    httpApi,
                    logger,
                  });
                }

                return (
                  <Provider context={widgetContext.current}>
                    <SaveSearch
                      source={ESource.Card}
                      jsonQuery={jsonQuery}
                      defaultTitle={defaultTitle || undefined}
                      defaultFrequency={EFrequency.Hourly}
                      defaultEmail={userEmail || undefined}
                      defaultSubscribeToNews={true}
                      hideLicense={hideLicense}
                      customTitleLabel="Название"
                      customSubmitButtonText={submitButtonText}
                      onSubscribed={onSubscribed}
                    />
                  </Provider>
                );
              }}
            </SaveSearchWidgetLoadable>
          </WidgetErrorBoundary>
        </div>
      }
    />
  );
};
